'use client';

import { useRouter, useParams } from 'next/navigation';
import { useYmmFormContext } from './context/ymm-form-context';
import { SelectField } from '@/vibes/soul/form/select-field';

import { Loader2 } from 'lucide-react';

import { Button } from '@/vibes/soul/primitives/button';
import clsx from 'clsx';

/** Strip tag prefix (yr_, mk_, md_) from field value for URL param. */
function toSearchParamValue(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

export interface VehicleFilterFormProps {
  className?: string;
  /** PLP: shorter heading and primary reset button; full-width layout */
  variant?: 'default' | 'plp';
}

export function VehicleFilterForm({ variant = 'default', className }: VehicleFilterFormProps) {
  const router = useRouter();
  const params = useParams();
  const locale = params?.locale as string | undefined;
  const pathname = locale ? `/${locale}/fitments` : '/fitments';
  const {
    isLoading,
    formData,
    fieldState,
    setYear,
    setMake,
    setModel,
    resetSelections,
    persistYmmSelection,
    clearPreserveFromRestore,
    appDisabled
  } = useYmmFormContext();

  const { year, make, model } = formData;

  const onYearChange = (value: string) => {
    setYear(value);
  };

  const onMakeChange = (value: string) => {
    setMake(value);
  };

  const onModelChange = (value: string) => {
    setModel(value);
  };

  const onReset = () => {
    resetSelections();
  };

  const onSearch = () => {
    const yr = fieldState.year.current_val ? toSearchParamValue(fieldState.year.current_val, 'yr_') : '';
    const mk = fieldState.make.current_val ? toSearchParamValue(fieldState.make.current_val, 'mk_') : '';
    const md = fieldState.model.current_val ? toSearchParamValue(fieldState.model.current_val, 'md_') : '';

    if (yr && mk && md) {
      persistYmmSelection();
      clearPreserveFromRestore();
      router.push(`${pathname}?yr=${yr}&mk=${mk}&md=${md}`);
    }
  };

  if (appDisabled) return null;

  const isPlp = variant === 'plp';

  return (
    <section
      className={clsx(
        'bg-primary-black w-full @container',
        isPlp ? 'max-w-none' : 'max-w-2xl mx-auto',
        className,
      )}
    >
      <form
        className={clsx('flex flex-col gap-4 px-6 py-8 @3xl:flex-row @3xl:flex-wrap @5xl:flex-nowrap @3xl:items-center @7xl:gap-8',
          isPlp ? 'max-w-2xl mx-auto @5xl:p-6' : '@5xl:pt-10 @5xl:pb-8 @5xl:px-12',
        )}
        onSubmit={(e) => {
          e.preventDefault();
          onSearch();
        }}
      >
        <div className={clsx('text-2xl font-bold font-heading text-white @3xl:basis-full',
          isPlp ? '@5xl:basis-[400px]' : '@5xl:basis-1/2',
        )}>
          {isPlp ? (
            'Search by Vehicle'
          ) : (
            <>
              One Truck to Rule Them All -{' '}
              <span className="text-[hsl(var(--primary-accent))]">Let&apos;s Find Yours</span>
            </>
          )}
        </div>
        <div className="flex flex-col @3xl:flex-row items-center gap-4 @3xl:basis-full @5xl:gap-8 @3xl:justify-between">
          <div className={clsx(`relative w-full`,
            isLoading.year && 'pointer-events-none',
            isLoading.year && 'hover:after:translate-x-0')
          }>
            <SelectField
              key={`ymm-year-${fieldState.year.current_val}-${(year?.options?.length ?? 0)}`}
              hideLabel={true}
              label="Year"
              className="bg-white"
              name="year"
              value={fieldState.year.current_val}
              placeholder={year.placeholder || 'Year'}
              options={year?.options || []}
              onValueChange={onYearChange}
              colorScheme="light"
            />
            {isLoading.year && (
              <span
                className={clsx(
                  'absolute inset-0 grid place-content-center transition-all duration-300 ease-in-out translate-y-0 opacity-100',
                )}
              >
                <Loader2 className="animate-spin text-[var(--button-loader-icon,hsl(var(--foreground)))]" />
              </span>
            )}
          </div>
          <div className={clsx(`relative w-full`,
            isLoading.make && 'pointer-events-none',
            isLoading.make && 'hover:after:translate-x-0')
          }>
            <SelectField
              key={`ymm-make-${fieldState.make.current_val}-${(make.options?.length ?? 0)}`}
              hideLabel={true}
              label="Make"
              className="bg-white"
              name="make"
              value={fieldState.make.current_val}
              placeholder={make.placeholder || 'Make'}
              options={make.options || []}
              onValueChange={onMakeChange}
              colorScheme="light"
              disabled={make.options?.length === 0}
            />
            {isLoading.make && (
              <span
                className={clsx(
                  'absolute inset-0 grid place-content-center transition-all duration-300 ease-in-out translate-y-0 opacity-100',
                )}
              >
                <Loader2 className="animate-spin text-[var(--button-loader-icon,hsl(var(--foreground)))]" />
              </span>
            )}
          </div>
          <div className={clsx(`relative w-full`,
            isLoading.model && 'pointer-events-none')
          }>
            <SelectField
              key={`ymm-model-${fieldState.model.current_val}-${(model.options?.length ?? 0)}`}
              hideLabel={true}
              label="Model"
              className="bg-white"
              name="model"
              value={fieldState.model.current_val}
              placeholder={model.placeholder || 'Model'}
              options={model.options || []}
              onValueChange={onModelChange}
              colorScheme="light"
              disabled={model.options?.length === 0}
            />
            {isLoading.model && (
              <span
                className={clsx(
                  'absolute inset-0 grid place-content-center transition-all duration-300 ease-in-out translate-y-0 opacity-100',
                )}
              >
                <Loader2 className="animate-spin text-[var(--button-loader-icon,hsl(var(--foreground)))]" />
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-4 @7xl:gap-8">
          <Button variant="primary" shape="square" size="small" type="button" onClick={onSearch}>
            Search
          </Button>
          <Button
            variant={isPlp ? 'primary' : 'secondary'}
            shape="square"
            size="small"
            className={isPlp ? undefined : 'text-white bg-transparent border-white'}
            type="button"
            onClick={onReset}
          >
            Reset
          </Button>
        </div>
      </form>
    </section>
  );
}